import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { StaticMap } from "@freightsimple/ui-core/Display/StaticMap";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { nameAccessorialsExplicitly } from "@/Data/nameAccessorialsExplicitly";
import { describeLocationType } from "@/Data/describeLocationType";
import { LocationsSummaryGroup } from "@/Shared/Locations/CustomComponents/LocationsSummaryGroup";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { describeHours } from "@/Helpers/describeHours";
import { fullStateOrProvinceName } from "@/Helpers/fullStateOrProvinceName";
import {
  describeLoadingCapabilities,
  hasLiftGateAccessorial,
} from "@/Shared/Locations/Helpers/loadingCapabilities";

interface LocationBox2Props {
  location: AddLocationLocationType;
  context: LocationContext;
  locationHasDock?: LoadingCapabilitiesAvailability | undefined;
  locationHasForklift?: LoadingCapabilitiesAvailability | undefined;
}
function BookShipmentLocationBox2(props: LocationBox2Props) {
  const { businessName, address } = props.location;

  const accessorials = nameAccessorialsExplicitly(
    props.location.accessorials,
    props.context,
    props.location.locationType ?? LocationType.Warehouse,
  );

  const hasLiftGate = hasLiftGateAccessorial(props.location.accessorials);

  const hoursDescription = describeHours(props.location.hours);
  const locationTypeDescription = describeLocationType(
    props.location.locationType ?? LocationType.Warehouse,
    props.location.distributionWarehouseBrand,
  );

  // No location at all means nothing worth summarising, so the card stays away.
  // Coordinates are a separate question: a location can resolve without them,
  // and then the card still has an address, hours and services to show — only
  // the map drops out.
  if (props.location.latitudeLongitude === undefined) {
    return <></>;
  }

  const latitude = props.location.latitudeLongitude.latitude;
  const longitude = props.location.latitudeLongitude.longitude;

  return (
    <Panel collapseOnNarrow>
      {/* A plain flex row rather than <Row>: on a phone the fixed-width map and
          the details can't sit side by side, so below the same 640px the card
          goes flush the two stack and the map spans the full width. <Row> has no
          responsive direction, hence the raw classes. Narrow mode comes from
          the `.narrow-cards` ancestor class (measured width), matching ui-core
          Card's collapseOnNarrow. */}
      <div className="flex items-start gap-6 in-[.narrow-cards]:flex-col">
        {latitude !== undefined && longitude !== undefined && (
          <div className="in-[.narrow-cards]:w-full">
            <StaticMap
              latitude={latitude}
              longitude={longitude}
              zoom={11}
              width={384}
              height={160}
            />
          </div>
        )}
        <Stack gap={4} align="start">
          <T size="lg" weight="medium">
            {businessName}
          </T>
          <Row gap={6} align="start">
            <Stack gap={3} align="start">
              <LocationsSummaryGroup
                label="Address"
                lines={[
                  address.addressLine,
                  address.addressLine2,
                  address.city,
                  fullStateOrProvinceName(address.stateOrProvinceCode || ""),
                  address.postalCode,
                ]}
              />
              <LocationsSummaryGroup
                label="Location Type"
                lines={[locationTypeDescription]}
              />
            </Stack>

            <Stack gap={3} align="start">
              <LocationsSummaryGroup
                label="Special Services"
                lines={accessorials}
              />
              {/* A lift gate settles the loading question — the truck brings
                  its own way to get the freight down, so whether the location
                  has a dock or a forklift stops mattering. */}
              {!hasLiftGate && (
                <LocationsSummaryGroup
                  label="Loading"
                  lines={[
                    describeLoadingCapabilities(
                      props.locationHasDock ??
                        LoadingCapabilitiesAvailability.Unknown,
                      props.locationHasForklift ??
                        LoadingCapabilitiesAvailability.Unknown,
                    ),
                  ]}
                />
              )}
              <LocationsSummaryGroup label="Hours" lines={[hoursDescription]} />
            </Stack>
          </Row>
        </Stack>
      </div>
    </Panel>
  );
}
export default BookShipmentLocationBox2;
